import axios from "axios";
import {
  Action,
  action,
  createStore,
  createTypedHooks,
  persist,
  Thunk,
  thunk,
} from "easy-peasy";

import { isCartConverted } from "helpers/convertedCarts";
import {
  CurrencyMetadataMap,
  CurrencyRates,
  normalizeCurrencyCode,
  SupportedCurrency,
  syncCurrencyMetadata,
} from "helpers/currency";
import { Car, InlineScriptVars } from "helpers/global-types";
import { Tracking } from "helpers/tracking";

const parsePriceValue = (value: number | string | undefined | null): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const getVehiclePrice = (vehicle: Vehicle, roundtrip?: number) => {
  // Prefer separate one-way / round-trip prices when both are present — that's
  // the common case. Fall back to a single `price` field for legacy vehicles
  // that only expose one price, and finally to whichever separate price is
  // available alone. Returns "0.00" when nothing is set.
  const hasSeparatePrices =
    vehicle.oneway_price !== undefined && vehicle.roundtrip_price !== undefined;

  if (hasSeparatePrices) {
    return roundtrip ? vehicle.roundtrip_price : vehicle.oneway_price;
  }
  if (vehicle.price !== undefined) return vehicle.price;
  if (vehicle.oneway_price !== undefined) return vehicle.oneway_price;
  if (vehicle.roundtrip_price !== undefined) return vehicle.roundtrip_price;
  return "0.00";
};

const mapVehicle = (vehicle: Vehicle, roundtrip?: number): Car => ({
  carName: vehicle.name,
  price: parsePriceValue(getVehiclePrice(vehicle, roundtrip)),
  vehicleId: vehicle.vehicle_id,
  numberOfSeats: parseFloat(vehicle.passenger_limit),
  picturePath: vehicle.image,
  oneSeatAllowed: false,
  noMoreThenAmountOfPeople:
    vehicle.type === "shuttle" ? Number(vehicle.passenger_limit) : Infinity,
  isShuttle: vehicle.type === "shuttle",
  description: vehicle.description,
  tripType: roundtrip ? "Round trip" : "One way",
});

export { mapVehicle };

const flattenCart = (data: CartResponse): Cart => {
  const { contact, ...rest } = data;
  return { ...rest, ...(contact ?? {}) };
};

/**
 * Minimal surface of store actions that applyCartToStore needs. Typed as a
 * structural interface so this works the same whether you call it from a
 * thunk (where `actions` is Easy-Peasy's action map) or from a hook (where
 * each action is pulled individually via useStoreActions).
 */
type CartSyncActions = {
  setCart: (cart: Cart) => void;
  setCartToken: (token: string) => void;
  setSelectedCar: (car: Car) => void;
  setSelectedAddons?: (addons: AddonCart[]) => void;
  setNumberOfPassengers: (count: number) => void;
  setIsCustomDestination: (v: boolean) => void;
  setCustomDestination: (v: string) => void;
  setIsLocationAirport: (v: boolean) => void;
  setIsDestinationAirport: (v: boolean) => void;
  setCoupon?: (coupon: Coupon | null) => void;
};

/**
 * Apply a freshly-fetched cart payload to the client store. Centralizes what
 * used to be four copy-pasted blocks (updateCartThunk, getCartThunk,
 * getTokenCartThunk, useTokenCart) that had already drifted from each other.
 *
 * Pass `applyCoupon: true` when the source response is authoritative for the
 * coupon (update-cart flow). Pass `applyAddons: true` from hooks that load
 * carts directly from a token where the server includes addons.
 */
/**
 * Classify an axios / thrown error into a user-facing message + retryability.
 * Server-side 4xx responses carry a structured `{ code, message }` body for the
 * known business failures (fx_unavailable, shuttle_cutoff, coupon_usage_exhausted,
 * invalid_booking_date, invalid_departure_date, site_unresolved,
 * stripe_key_misconfigured, booking_insert_failed_after_charge, etc.) — we
 * surface those directly. Other errors get a generic friendly message.
 */
export type CartErrorKind = {
  code: string;
  message: string;
  retryable: boolean;
};

// Codes where the user must take action (switch card, remove coupon, etc.);
// showing a blanket "try again" is misleading for these.
const ACTIONABLE_ERROR_CODES = new Set([
  "card_declined",
  "coupon_usage_exhausted",
  "shuttle_cutoff",
  "fx_unavailable",
  "amount_check_failed",
  "invalid_booking_date",
  "invalid_departure_date",
  "booking_too_soon",
  "site_unresolved",
  "booking_insert_failed_after_charge",
  "stripe_key_misconfigured",
]);

export const classifyCartError = (err: unknown): CartErrorKind => {
  // Axios error shape
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyErr = err as any;
  const status: number | undefined = anyErr?.response?.status;
  const body = anyErr?.response?.data;
  const code: string | undefined =
    typeof body === "object" && body ? body.code : undefined;
  const serverMessage: string | undefined =
    typeof body === "object" && body ? body.message : undefined;

  if (code && ACTIONABLE_ERROR_CODES.has(code)) {
    return {
      code,
      message:
        serverMessage ??
        "We could not complete that step. Please adjust and try again.",
      retryable: false,
    };
  }

  // Network error (no response) → transient
  if (anyErr?.code === "ERR_NETWORK" || status === undefined) {
    return {
      code: "network_error",
      message: "Connection problem. Please check your internet and try again.",
      retryable: true,
    };
  }

  // 5xx → transient
  if (status >= 500) {
    return {
      code: code ?? "server_error",
      message:
        serverMessage ??
        "Something went wrong on our end. Please try again in a moment.",
      retryable: true,
    };
  }

  // Other 4xx with structured body → actionable
  if (status >= 400 && status < 500) {
    return {
      code: code ?? "bad_request",
      message: serverMessage ?? "We couldn't process that request.",
      retryable: false,
    };
  }

  return {
    code: code ?? "unknown",
    message: serverMessage ?? "Something went wrong. Please try again.",
    retryable: true,
  };
};

// Serialization gate for tokenless cart writes.
//
// Problem this guards against: on a fresh visitor, rapid step-1 actions
// (pickup → destination → passengers → Next) each dispatch updateCartThunk
// before the previous response returns. All of them read `cartToken: null`
// from the store, so all four `update-cart` POSTs go out without a token.
// The server has no PHP session lock in REST context (sjd_load_cart_session
// touches $_SESSION but session_start is never called there), so each
// tokenless POST takes the INSERT branch in sjd_set_cart() and creates a
// new cart row with its own generated token. The client only keeps the
// last token to land; the other rows are orphaned in wp_sjd_cart_transients.
//
// This promise gates concurrent tokenless writes: the first call creates
// the cart and resolves with its token; subsequent calls await it and
// then proceed with that token, which routes them to the UPDATE branch
// server-side instead of INSERT. Once a token is in store, this gate
// is a no-op.
//
// Module-scoped because the booking app has a single cart per page; if
// that ever changes (multi-cart UI), keying this by some cart-instance
// id would be the next step.
let pendingCartCreate: Promise<string | null> | null = null;

export const applyCartToStore = (
  flat: Cart,
  actions: CartSyncActions,
  options: {
    applyCoupon?: boolean;
    applyAddons?: boolean;
    prevCart?: Cart | null;
  } = {}
): void => {
  // Race-condition defense: when two update-cart calls fire in the same tick
  // (typically onSubmit + a useCurrency-style refresh), the server's session
  // lock can let the second one read the session before the first's write
  // committed and return a cart with location/destination=undefined. Replacing
  // the populated cart with that empty response blows away state the user just
  // entered. Skip the apply when the response is clearly empty AND we already
  // had a populated cart.
  const responseLooksEmpty =
    !flat.location &&
    !flat.destination &&
    !flat.is_custom_location &&
    !flat.custom_location &&
    !flat.vehicle &&
    !flat.booking_id;
  const prevHadCart =
    !!options.prevCart?.location ||
    !!options.prevCart?.destination ||
    !!options.prevCart?.vehicle;
  if (responseLooksEmpty && prevHadCart) {
    // eslint-disable-next-line no-console
    console.warn(
      "[sjd-cart] ignoring empty cart response (likely a session-lock race) — keeping previous state",
      { flat, prevCart: options.prevCart }
    );
    return;
  }

  // Partial-staleness defense. The all-empty check above only catches a
  // response where EVERY field is gone; the shape that actually reached
  // production is subtler — a mid-write read that still carries `vehicle` or
  // `booking_id` but has lost `location`/`destination`. That sailed straight
  // through to setCart, after which useStepGuard read the cart as incomplete
  // and redirected the user off their current step. Because every InputBox
  // Controller sets shouldUnregister, that unmount deletes the field values
  // outright — this is the "page refreshed and wiped my input" report.
  //
  // Treating a dropped field as stale is safe because no live server path
  // clears these: every update-cart branch that touches location/destination
  // *assigns* it (update-cart-controller.php:185-205), and
  // clean_custom_location() — the only code that unsets them — sits inside a
  // commented-out block (:99-117) and is unreachable. A response that drops a
  // field the previous cart had is therefore stale by definition.
  //
  // The custom-destination carve-out matters: those carts legitimately have no
  // `destination`, so we only treat a missing one as suspect when the response
  // also denies being a custom-location booking.
  const wouldClearLocation = !!options.prevCart?.location && !flat.location;
  const wouldClearDestination =
    !!options.prevCart?.destination &&
    !flat.destination &&
    !flat.is_custom_location &&
    !flat.custom_location;
  if (wouldClearLocation || wouldClearDestination) {
    // eslint-disable-next-line no-console
    console.warn(
      "[sjd-cart] ignoring partial cart response that would clear a populated field — keeping previous state",
      {
        wouldClearLocation,
        wouldClearDestination,
        flat,
        prevCart: options.prevCart,
      }
    );
    return;
  }

  actions.setCart(flat);
  if (flat.token) actions.setCartToken(flat.token);
  if (flat.vehicle) {
    actions.setSelectedCar(mapVehicle(flat.vehicle, flat.roundtrip));
  }
  if (flat.passengers !== undefined) {
    actions.setNumberOfPassengers(Number(flat.passengers));
  }
  actions.setIsCustomDestination(
    Boolean(flat.is_custom_location ?? flat.custom_location)
  );
  if (flat.custom_location) {
    actions.setCustomDestination(flat.custom_location);
  }
  if (flat.location) {
    actions.setIsLocationAirport(Number(flat.location.is_airport) === 1);
  }
  if (flat.destination) {
    actions.setIsDestinationAirport(Number(flat.destination.is_airport) === 1);
  }
  if (options.applyCoupon && flat.coupon && actions.setCoupon) {
    actions.setCoupon(flat.coupon);
  }
  if (options.applyAddons && flat.addons && actions.setSelectedAddons) {
    actions.setSelectedAddons(flat.addons);
  }
};

import {
  Addon,
  AddonCart,
  Cart,
  CartPayload,
  CartResponse,
  Country,
  Coupon,
  CouponResponse,
  Fee,
  Location,
  State,
  Vehicle,
  VehicleParams,
} from "./types";

export interface StoreModel {
  // locations
  locations: Location[];
  setLocations: Action<this, this["locations"]>;
  getLocationsThunk: Thunk<this>;
  // addons
  addons: Addon[];
  setAddons: Action<this, this["addons"]>;
  getAddonsThunk: Thunk<this, { vehicle_id?: string }>;
  currentCarAddons: Addon[];
  selectedAddons: AddonCart[];
  setSelectedAddons: Action<this, this["selectedAddons"]>;
  setCurrentCarAddons: Action<this, this["currentCarAddons"]>;
  //  vehicles
  vehicles: Car[];
  setVehicles: Action<this, this["vehicles"]>;
  getVehiclesThunk: Thunk<this, VehicleParams>;
  // coupon
  coupon: null | Coupon;
  setCoupon: Action<this, this["coupon"]>;
  validateCouponThunk: Thunk<
    StoreModel,
    { code: string },
    unknown,
    StoreModel,
    Promise<CouponResponse | undefined>
  >;
  // cart
  cart: null | Cart;
  setCart: Action<this, this["cart"]>;
  cartToken: string | null;
  setCartToken: Action<this, this["cartToken"]>;
  // Last user-visible error from a cart / payment thunk. `retryable: true` means
  // transient (network, 5xx, rate limit) — the UI should show a "try again" button.
  // `retryable: false` means actionable (card declined, coupon exhausted, shuttle
  // cutoff, etc.) — the user needs to fix something before the next attempt.
  cartError: null | { code: string; message: string; retryable: boolean };
  setCartError: Action<this, this["cartError"]>;
  clearCartError: Action<this>;
  updateCartThunk: Thunk<this, CartPayload>;
  getCartThunk: Thunk<this>;
  getTokenCartThunk: Thunk<this, string>;
  // currency
  currency: SupportedCurrency;
  currencyManuallySelected: boolean;
  setCurrency: Action<this, string | SupportedCurrency>;
  supportedCurrencies: SupportedCurrency[];
  setSupportedCurrencies: Action<this, SupportedCurrency[]>;
  currencyMetadata: CurrencyMetadataMap;
  setCurrencyMetadata: Action<this, CurrencyMetadataMap>;
  currencyRates: CurrencyRates;
  currencyRatesFetchedAt: number | null;
  currencyRatesExpiresAt: number | null;
  currencyRatesLoading: boolean;
  setCurrencyRates: Action<this, CurrencyRates>;
  setCurrencyRatesFetchedAt: Action<this, number | null>;
  setCurrencyRatesExpiresAt: Action<this, number | null>;
  setCurrencyRatesLoading: Action<this, boolean>;
  fetchCurrencyRatesThunk: Thunk<this>;
  // countries
  countries: null | Country[];
  setCountries: Action<this, this["countries"]>;
  selectedCountries: null | Country;
  setSelectedCountries: Action<this, this["selectedCountries"]>;
  getCountriesThunk: Thunk<this>;
  // country states
  states: null | State[];
  setStates: Action<this, this["states"]>;
  getStatesThunk: Thunk<this, { country_id?: string } | undefined>;
  // booking
  bookingsCreateThunk: Thunk<this>;
  // payments
  paymentsResponse: null;
  submitPaymentMethodThunk: Thunk<
    this,
    { method: "stripe"; currency?: string; timezone?: string }
  >;
  // fee
  fee: null | Fee;
  setFee: Action<this, this["fee"]>;
  getFeeThunk: Thunk<
    this,
    {
      addons?: AddonCart[];
      passengers?: number;
      vehicle_id?: number;
      coupon_id?: number;
    }
  >;
  bookingId: number | null;
  setBookingId: Action<this, this["bookingId"]>;
  paymentIntentId: string | null;
  setPaymentIntentId: Action<this, this["paymentIntentId"]>;
  // form data model
  customDestination: string | null;
  setCustomDestination: Action<this, this["customDestination"]>;
  isCustomDestination: boolean;
  setIsCustomDestination: Action<this, this["isCustomDestination"]>;
  isLocationAirport: boolean;
  setIsLocationAirport: Action<this, this["isLocationAirport"]>;
  // isDateFormError / isTimeFormError removed: two DatePickers and two
  // TimePickers all wrote their verdict into these single shared strings from
  // effects that re-ran on every render, so the last writer won and a valid
  // field routinely erased another field's genuine error. Validation is now
  // per-field via react-hook-form `validate` rules, which handleSubmit honours
  // natively — see TimePicker.tsx's rulesObject.
  isDestinationAirport: boolean;
  setIsDestinationAirport: Action<this, this["isDestinationAirport"]>;
  // selected Car
  selectedCar: Car | null;
  setSelectedCar: Action<this, this["selectedCar"]>;
  // stepper
  steps: string[];
  setSteps: Action<this, this["steps"]>;
  //  affiliate Id
  affiliateId?: string;
  setAffiliateId: Action<this, this["affiliateId"]>;
  // marketing attribution (hubspotutk + UTMs + Bing/Google click ids)
  tracking: Tracking;
  setTracking: Action<this, this["tracking"]>;
  // loading
  isGlobalLoading: boolean;
  setIsGlobalLoading: Action<this, this["isGlobalLoading"]>;
  // True from a Next click until the destination step signals it is ready.
  // Lets the destination step (a different component instance than the one
  // that started the transition) own hiding the loader, instead of a fixed
  // timer — while leaving page-load and in-step loading untouched.
  navPending: boolean;
  setNavPending: Action<this, this["navPending"]>;
  // Optional context message shown by the global Loader (shimmering text).
  // Set via setLoadingMessage(string) to also turn on isGlobalLoading; or
  // pass null to clear the message without touching isGlobalLoading. Most
  // callers should use setLoadingMessage("…") instead of
  // setIsGlobalLoading(true) — the latter falls back to a generic
  // "Loading…" string which is rarely the right answer.
  loadingMessage: string | null;
  setLoadingMessage: Action<this, this["loadingMessage"]>;
  // total loading
  isTotalLoading: boolean;
  setIsTotalLoading: Action<this, this["isTotalLoading"]>;
  // numberOfPassengers:
  numberOfPassengers: number;
  setNumberOfPassengers: Action<this, this["numberOfPassengers"]>;
  // inline script vars
  inlineScriptVars: null | InlineScriptVars;
  setInlineScriptVars: Action<this, this["inlineScriptVars"]>;
  getInlineScriptVars: Thunk<this>;
}

export const storeModel: StoreModel = {
  // locations
  locations: [],
  setLocations: action((state, payload) => {
    state.locations = payload;
  }),
  getLocationsThunk: thunk(async (actions) => {
    const { data, status } = await axios.get<Location[]>("locations");

    if (status === 200) {
      actions.setLocations(data);
    }
  }),
  // addons
  addons: [],
  setAddons: action((state, payload) => {
    state.addons = payload;
  }),
  currentCarAddons: [],
  setCurrentCarAddons: action((state, payload) => {
    state.currentCarAddons = payload;
  }),
  selectedAddons: [],
  setSelectedAddons: action((state, payload) => {
    state.selectedAddons = payload;
  }),
  getAddonsThunk: thunk(async (actions, payload, { getState }) => {
    const storeState = getState() as StoreModel;
    const selectedCurrency = normalizeCurrencyCode(storeState.currency);
    const cartToken = storeState.cartToken;

    const { data, status } = await axios.get<Addon[]>("addons", {
      params: {
        ...payload,
        ...(cartToken ? { token: cartToken } : {}),
        ...(selectedCurrency
          ? { currency: selectedCurrency.toUpperCase() }
          : {}),
      },
    });

    if (status === 200) {
      if (payload.vehicle_id) {
        actions.setCurrentCarAddons(data);
      } else {
        actions.setAddons(data);
      }
    }
  }),
  // vehicles
  vehicles: [],
  setVehicles: action((state, payload) => {
    state.vehicles = payload;

    if (state.selectedCar) {
      const updatedSelection = payload.find(
        (vehicle) =>
          String(vehicle.vehicleId) === String(state.selectedCar?.vehicleId)
      );

      if (updatedSelection) {
        state.selectedCar = {
          ...state.selectedCar,
          ...updatedSelection,
        };
      }
    }
  }),
  getVehiclesThunk: thunk(async (actions, payload, { getStoreState }) => {
    const state = getStoreState() as StoreModel;
    const selectedCurrency = normalizeCurrencyCode(state.currency);
    const cartToken = state.cartToken;

    const { data, status } = await axios.get<Vehicle[]>("vehicles", {
      params: {
        ...payload,
        ...(cartToken ? { token: cartToken } : {}),
        ...(selectedCurrency
          ? { currency: selectedCurrency.toUpperCase() }
          : {}),
      },
    });
    const { cart } = state;
    const bookingDate = cart?.booking_date;
    let hideShuttle = false;
    // Hub operates in Cabo San Lucas (America/Mazatlan, MST UTC-7 year-round).
    // Mexico abolished DST in 2022 — don't swap in America/Denver, which
    // shifts at DST boundaries. Mirrors sjd_shuttle_cutoff_applies() server-side.
    if (bookingDate) {
      const nowMt = new Date(
        new Date().toLocaleString("en-US", { timeZone: "America/Mazatlan" })
      );
      const booking = new Date(bookingDate);
      const tomorrow = new Date(nowMt);
      tomorrow.setDate(nowMt.getDate() + 1);
      if (
        booking.getFullYear() === tomorrow.getFullYear() &&
        booking.getMonth() === tomorrow.getMonth() &&
        booking.getDate() === tomorrow.getDate() &&
        nowMt.getHours() >= 20
      ) {
        hideShuttle = true;
      }
    }

    if (status === 200) {
      actions.setVehicles(
        data
          .filter((vehicle) => !(hideShuttle && vehicle.type === "shuttle"))
          .map((vehicle) => {
            const v = {
              ...vehicle,
              image: vehicle.image,
            } as Vehicle;
            return mapVehicle(v, cart?.roundtrip);
          })
      );
      return data;
    }
  }),
  // coupon
  coupon: null,
  setCoupon: action((state, payload) => {
    state.coupon = payload;
  }),
  validateCouponThunk: thunk(async (actions, payload, { getState }) => {
    const cartToken = getState().cartToken;
    const selectedCurrency = normalizeCurrencyCode(getState().currency);

    const { status, data } = await axios.post<CouponResponse>(
      "validate-coupon",
      {
        ...payload,
        ...(cartToken ? { token: cartToken } : {}),
        ...(selectedCurrency
          ? { currency: selectedCurrency.toUpperCase() }
          : {}),
      } as { code: string }
    );

    if (status === 200) {
      return data;
    }
  }),
  // fee
  fee: null,
  setFee: action((state, payload) => {
    state.fee = payload;
  }),

  getFeeThunk: thunk(async (actions, payload, { getState }) => {
    const cartToken = getState().cartToken;
    const selectedCurrency = normalizeCurrencyCode(getState().currency);

    try {
      const { data, status } = await axios.post<Fee>(
        "order-summary",
        {
          ...payload,
          ...(cartToken ? { token: cartToken } : {}),
          ...(selectedCurrency
            ? { currency: selectedCurrency.toUpperCase() }
            : {}),
        },
        {}
      );

      if (status === 200 && data && typeof data.subtotal !== "undefined") {
        actions.setFee(data);
      }
    } catch (error) {
      console.error("Error fetching fee:", error);
    }
  }),
  // cart
  cart: null,
  setCart: action((state, payload) => {
    state.cart = payload;
    if (payload?.currency) {
      const normalized = normalizeCurrencyCode(payload.currency);
      if (state.cart) {
        state.cart.currency = normalized;
      }
      // Keep top-level state.currency and cart.currency in sync. Previously
      // state.currency only updated when the user had NOT manually selected a
      // currency — but cart.currency was written unconditionally. A manual EUR
      // selection followed by a cart reload that returned USD left state.currency=EUR
      // while cart.currency=USD, so the rest of the UI disagreed with itself.
      //
      // New rule: if the server's cart.currency genuinely differs from the manually
      // selected one, the server wins (it's the authoritative source for the cart
      // it's returning). Clear the manual flag so subsequent server updates are
      // trusted too.
      if (
        state.currencyManuallySelected &&
        normalized !== normalizeCurrencyCode(state.currency)
      ) {
        state.currency = normalized;
        state.currencyManuallySelected = false;
      } else if (!state.currencyManuallySelected) {
        state.currency = normalized;
      }
    }
  }),
  cartToken: null,
  setCartToken: action((state, payload) => {
    state.cartToken = payload;
  }),
  cartError: null,
  setCartError: action((state, payload) => {
    state.cartError = payload;
  }),
  clearCartError: action((state) => {
    state.cartError = null;
  }),
  updateCartThunk: thunk(async (actions, payload, { getState }) => {
    // If we don't have a cart token yet AND another tokenless write is
    // already in flight, await it so we inherit its token instead of
    // racing it into a parallel INSERT on the server. See the comment on
    // `pendingCartCreate` above for the full failure mode this guards.
    if (!getState().cartToken && pendingCartCreate) {
      try {
        await pendingCartCreate;
      } catch {
        /* upstream call failed; we'll try our own request below */
      }
    }

    const storeAffiliateId = Number(getState().affiliateId);
    const storeAffiliateIdPayload = !isNaN(storeAffiliateId)
      ? { external_id: storeAffiliateId }
      : {};
    // Marketing attribution — persisted on the cart so the hub can attach it to
    // the HubSpot deal and tie the contact to its analytics session. Sent on
    // every update like external_id; the backend stores idempotently.
    const trackingPayload = getState().tracking ?? {};
    const cartToken = getState().cartToken;
    const selectedCurrency = normalizeCurrencyCode(getState().currency);
    const prevCart = getState().cart;

    // Another tab has already paid for this cart (see helpers/convertedCarts).
    // Writing to it would strip its booking_id server-side, so let go of it
    // instead; the next write from this tab starts a fresh cart.
    if (cartToken && isCartConverted(cartToken)) {
      actions.setCartToken(null);
      actions.setCart(null);
      return undefined;
    }

    actions.clearCartError();
    const requestBody = {
      ...storeAffiliateIdPayload,
      ...trackingPayload,
      ...(cartToken ? { token: cartToken } : {}),
      ...(selectedCurrency ? { currency: selectedCurrency.toUpperCase() } : {}),
      ...payload,
    };
    // eslint-disable-next-line no-console
    console.log("[sjd-cart] update-cart →", {
      payload,
      requestBody,
      prevDestination: prevCart?.destination,
      prevLocation: prevCart?.location,
    });

    // If this call is the one that will create the cart row, register the
    // pending promise so concurrent thunks dispatched in the same tick can
    // wait on it. Resolve it with the token from the response (or null on
    // failure) so waiters can proceed either way.
    //
    // resolveCreate starts as a noop so TS can keep its type as a callable
    // throughout the function. Conditional assignment inside the Promise
    // executor confused control-flow narrowing into typing it `never` at
    // call sites; the noop sidesteps that and is harmless when isCreator
    // is false (we gate calls on isCreator below anyway).
    const isCreator = !cartToken && !pendingCartCreate;
    let resolveCreate: (token: string | null) => void = () => {};
    if (isCreator) {
      pendingCartCreate = new Promise<string | null>((resolve) => {
        resolveCreate = resolve;
      });
    }

    try {
      const { data, status } = await axios.post<Cart>(
        "update-cart",
        requestBody
      );
      if (status === 200) {
        const flat = flattenCart(data);
        // The tab let go of this cart while the request was in flight — step
        // 7 clears the token once the booking is through, and its own
        // un-awaited `{ step: 7 }` write can land after that. Applying the
        // response would put the finished cart's token back in the store, and
        // every later write from this tab would go to the paid cart.
        if (cartToken && getState().cartToken !== cartToken) {
          return flat;
        }
        // Re-read state AFTER the await — concurrent thunks may have written
        // populated state while we were in flight. Captured-at-dispatch state
        // is empty for every concurrent call in the same tick, which would
        // defeat the empty-response race guard below.
        const currentCart = getState().cart;
        // eslint-disable-next-line no-console
        console.log("[sjd-cart] update-cart ←", {
          status,
          responseLocation: flat.location,
          responseDestination: flat.destination,
          willClearDestination:
            Boolean(currentCart?.destination) && !flat.destination,
          willClearLocation: Boolean(currentCart?.location) && !flat.location,
          flat,
        });
        applyCartToStore(flat, actions, {
          applyCoupon: true,
          prevCart: currentCart,
        });
        if (isCreator) resolveCreate(flat.token ?? null);
        return flat;
      }
      if (isCreator) resolveCreate(null);
      return data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[sjd-cart] update-cart ✗", err);
      const classified = classifyCartError(err);
      actions.setCartError(classified);
      if (isCreator) resolveCreate(null);
      // Resolve undefined instead of rejecting — callers that want the error
      // inspect `state.cartError` rather than relying on Promise rejection.
      return undefined;
    } finally {
      // Always clear the gate so the next batch of tokenless writes (e.g.
      // a user clearing the cart and starting fresh) can re-arm it.
      if (isCreator) pendingCartCreate = null;
    }
  }),
  getCartThunk: thunk(
    async (actions, _payload, { getState }): Promise<undefined | Cart> => {
      const cartToken = getState().cartToken;
      const selectedCurrency = normalizeCurrencyCode(getState().currency);

      const { data, status } = await axios.post<CartResponse>("update-cart", {
        token: cartToken,
        ...(selectedCurrency
          ? { currency: selectedCurrency.toUpperCase() }
          : {}),
      });

      if (status === 200) {
        const flat = flattenCart(data);
        applyCartToStore(flat, actions, { prevCart: getState().cart });
        return flat;
      }
    }
  ),
  getTokenCartThunk: thunk(
    async (actions, token, { getState }): Promise<Cart | undefined> => {
      const selectedCurrency = normalizeCurrencyCode(getState().currency);

      const { data, status } = await axios.post<CartResponse>("cart", {
        token: token,
        ...(selectedCurrency
          ? { currency: selectedCurrency.toUpperCase() }
          : {}),
      });

      if (status === 200) {
        const flat = flattenCart(data);
        // Same rule as updateCartThunk: a response for a cart this tab has
        // since let go of is still returned (step 7 renders from it) but must
        // not re-adopt the token.
        if (getState().cartToken !== token) {
          return flat;
        }
        applyCartToStore(flat, actions, { prevCart: getState().cart });
        return flat;
      }
    }
  ),
  // currency
  currency: "usd",
  currencyManuallySelected: false,
  setCurrency: action((state, payload) => {
    const normalized = normalizeCurrencyCode(payload);
    state.currency = normalized;
    state.currencyManuallySelected = true;
    if (state.cart) {
      state.cart.currency = normalized;
    }
  }),
  supportedCurrencies: [],
  setSupportedCurrencies: action((state, payload) => {
    const normalizedList = payload
      .map((code) => normalizeCurrencyCode(code))
      .filter((code, index, self) => code && self.indexOf(code) === index);

    const fallbackList = normalizedList.length
      ? normalizedList
      : [normalizeCurrencyCode(state.currency || "usd")];
    state.supportedCurrencies = fallbackList;

    if (!fallbackList.includes(state.currency)) {
      const fallbackCurrency = fallbackList[0];
      state.currency = fallbackCurrency;
      if (state.cart) {
        state.cart.currency = fallbackCurrency;
      }
    }
  }),
  currencyMetadata: {},
  setCurrencyMetadata: action((state, payload) => {
    state.currencyMetadata = { ...payload };
    syncCurrencyMetadata(state.currencyMetadata);
  }),
  currencyRates: {},
  currencyRatesFetchedAt: null,
  currencyRatesExpiresAt: null,
  currencyRatesLoading: false,
  setCurrencyRates: action((state, payload) => {
    state.currencyRates = { ...payload };
  }),
  setCurrencyRatesFetchedAt: action((state, payload) => {
    state.currencyRatesFetchedAt = payload;
  }),
  setCurrencyRatesExpiresAt: action((state, payload) => {
    state.currencyRatesExpiresAt = payload;
  }),
  setCurrencyRatesLoading: action((state, payload) => {
    state.currencyRatesLoading = payload;
  }),
  fetchCurrencyRatesThunk: thunk(async (actions, _payload, { getState }) => {
    if (getState().currencyRatesLoading) {
      return;
    }

    actions.setCurrencyRatesLoading(true);
    try {
      const { data, status } = await axios.get<{
        rates?: Record<string, number>;
        fetchedAt?: number;
        expiresAt?: number | null;
        supportedCurrencies?: string[];
        currencyMetadata?: Record<
          string,
          { label?: string | null; locale?: string | null }
        >;
      }>("currency-rates", {
        params: {
          base: "USD",
        },
      });

      if (status === 200 && data?.rates) {
        const normalizedRates: CurrencyRates = {};
        Object.entries(data.rates).forEach(([code, value]) => {
          const normalizedCode = normalizeCurrencyCode(code);
          if (typeof value === "number" && Number.isFinite(value)) {
            normalizedRates[normalizedCode] = value;
          }
        });

        if (!normalizedRates.usd) {
          normalizedRates.usd = 1;
        }

        actions.setCurrencyRates(normalizedRates);

        const fetchedAt =
          typeof data.fetchedAt === "number" && Number.isFinite(data.fetchedAt)
            ? data.fetchedAt
            : Date.now();
        actions.setCurrencyRatesFetchedAt(fetchedAt);

        const expiresAt =
          typeof data.expiresAt === "number" && Number.isFinite(data.expiresAt)
            ? data.expiresAt
            : null;
        actions.setCurrencyRatesExpiresAt(expiresAt);

        if (Array.isArray(data.supportedCurrencies)) {
          actions.setSupportedCurrencies(data.supportedCurrencies);
        } else {
          const derivedCurrencies = Object.keys(normalizedRates);
          if (derivedCurrencies.length) {
            actions.setSupportedCurrencies(derivedCurrencies);
          }
        }

        if (
          data.currencyMetadata &&
          typeof data.currencyMetadata === "object"
        ) {
          const normalizedMetadata: CurrencyMetadataMap = {};

          Object.entries(data.currencyMetadata).forEach(([code, details]) => {
            const normalizedCode = normalizeCurrencyCode(code);
            if (!normalizedCode) return;

            normalizedMetadata[normalizedCode] = {
              label:
                details && typeof details.label === "string"
                  ? details.label
                  : undefined,
              locale:
                details && typeof details.locale === "string"
                  ? details.locale
                  : undefined,
            };
          });

          actions.setCurrencyMetadata(normalizedMetadata);
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          (typeof error.response?.data?.message === "string"
            ? error.response?.data?.message
            : null) ?? error.message;
        console.error("Failed to fetch currency rates:", message, error);
      } else {
        console.error("Failed to fetch currency rates", error);
      }
    } finally {
      actions.setCurrencyRatesLoading(false);
    }
  }),
  bookingId: null,
  setBookingId: action((state, payload) => {
    state.bookingId = payload;
  }),
  paymentIntentId: null,
  setPaymentIntentId: action((state, payload) => {
    state.paymentIntentId = payload;
  }),
  // countries
  countries: null,
  setCountries: action((state, payload) => {
    state.countries = payload;
  }),
  selectedCountries: null,
  setSelectedCountries: action((state, payload) => {
    state.selectedCountries = payload;
  }),
  getCountriesThunk: thunk(async (actions) => {
    const { data, status } = await axios.get<Country[]>("countries");

    if (status === 200) {
      actions.setCountries(data);
    }
  }),
  // countries state
  states: null,
  setStates: action((state, payload) => {
    state.states = payload;
  }),
  getStatesThunk: thunk(async (actions, payload) => {
    const { data, status } = await axios.get<State[]>("country-states", {
      params: { ...payload },
    });

    if (status === 200) {
      actions.setStates(data);
    }
  }),
  // bookings
  bookingsCreateThunk: thunk(async (actions, _payload, { getState }) => {
    const cartToken = getState().cartToken;
    actions.clearCartError();
    try {
      const { data } = await axios.post("bookings", { token: cartToken });
      if (data?.token) actions.setCartToken(data.token);
      return data;
    } catch (err) {
      // Mirrors submitPaymentMethodThunk, which has always done this.
      //
      // Without it, every structured rejection from bookings-controller —
      // shuttle_cutoff, booking_too_soon, coupon_usage_exhausted, the invalid
      // date codes, all of which ship a user-facing `message` and are listed in
      // ACTIONABLE_ERROR_CODES above — was thrown into StripePaymentElement's
      // catch, console.error'd, and discarded. The server was carefully
      // explaining why checkout could not proceed and the customer saw the Pay
      // button do nothing at all.
      const classified = classifyCartError(err);
      actions.setCartError(classified);
      return undefined;
    }
  }),
  // payments
  paymentsResponse: null,
  submitPaymentMethodThunk: thunk(async (actions, payload, { getState }) => {
    const cartToken = getState().cartToken;
    actions.clearCartError();
    try {
      const { data } = await axios.post("payments", {
        ...payload,
        ...(cartToken ? { token: cartToken } : {}),
      });
      if (data?.token) actions.setCartToken(data.token);
      return data;
    } catch (err) {
      const classified = classifyCartError(err);
      actions.setCartError(classified);
      return undefined;
    }
  }),
  // selected Car
  selectedCar: null,
  setSelectedCar: action((state, payload) => {
    state.selectedCar = payload;
  }),
  isCustomDestination: false,
  setIsCustomDestination: action((state, payload) => {
    state.isCustomDestination = payload;
  }),
  customDestination: null,
  setCustomDestination: action((state, payload) => {
    state.customDestination = payload;
  }),
  isDestinationAirport: false,
  setIsDestinationAirport: action((state, payload) => {
    state.isDestinationAirport = payload;
  }),
  isLocationAirport: false,
  setIsLocationAirport: action((state, payload) => {
    state.isLocationAirport = payload;
  }),
  // stepper
  steps: [],
  setSteps: action((state, payload) => {
    state.steps = payload;
  }),
  //  affiliate Id
  affiliateId: "",
  setAffiliateId: action((state, payload) => {
    state.affiliateId = payload;
  }),
  // marketing attribution
  tracking: {},
  setTracking: action((state, payload) => {
    state.tracking = payload;
  }),
  // loading
  isGlobalLoading: false,
  setIsGlobalLoading: action((state, payload) => {
    state.isGlobalLoading = payload;
    // Clear the contextual message when loading turns off so the next
    // load doesn't briefly flash the previous message before its caller
    // assigns a new one.
    if (!payload) {
      state.loadingMessage = null;
    }
  }),
  navPending: false,
  setNavPending: action((state, payload) => {
    state.navPending = payload;
  }),
  loadingMessage: null,
  setLoadingMessage: action((state, payload) => {
    state.loadingMessage = payload;
    // Setting a message implies loading is active. Clearing the message
    // does NOT implicitly turn loading off — callers that finish work
    // call setIsGlobalLoading(false), which also nulls the message.
    if (payload !== null && payload !== "") {
      state.isGlobalLoading = true;
    }
  }),
  // total loading
  isTotalLoading: false,
  setIsTotalLoading: action((state, payload) => {
    state.isTotalLoading = payload;
  }),
  //numberOfPassengers
  numberOfPassengers: 1,
  setNumberOfPassengers: action((state, payload) => {
    state.numberOfPassengers = payload;
  }),
  // inline script variables
  inlineScriptVars: null,
  setInlineScriptVars: action((state, payload) => {
    state.inlineScriptVars = payload;
  }),
  getInlineScriptVars: thunk(async (actions, _payload, { getState }) => {
    const inlineScriptVars = getState().inlineScriptVars;

    if (inlineScriptVars === null) {
      const { data, status } = await axios.get<InlineScriptVars>("script-vars");

      if (status === 200) {
        actions.setInlineScriptVars(data);
        return data;
      }
    } else {
      return inlineScriptVars;
    }
  }),
};

const persistConfig = {
  whitelist: [
    // api
    "addons",
    "vehicles",
    "cartToken",
    "currency",
    "currencyManuallySelected",
    "supportedCurrencies",
    "currencyRates",
    "currencyRatesFetchedAt",
    "currencyRatesExpiresAt",
    // local
    "bookingId",
    "paymentIntentId",
    "isCustomDestination",
    "currentCarAddons",
    "coupon",
  ],
  storage: sessionStorage,
};

const store = createStore(persist(storeModel, persistConfig), {
  name: "Global SJD Taxi Store",
});

export const { useStoreActions, useStoreState, useStoreDispatch } =
  createTypedHooks<StoreModel>();

export default store;
